/**
 * What each interaction kind SAYS, and what answering it will do — as pure data.
 *
 * WHY THIS IS A MODULE AND NOT STRINGS IN JSX. Three of the rules below are correctness rules, not
 * wording preferences, and every one of them is invisible in a screenshot:
 *
 *   1. `blocking` is read off the FRAME, never derived from `kind` (§5.4 footnotes ¹²). `approve_plan`
 *      is blocking inside a running write run and advisory on a plan-mode run; `approve_changeset`
 *      and `approve_budget` are ALWAYS advisory. A UI that keys the "your run is waiting" treatment
 *      off `kind` is wrong for three of the eight kinds and looks right for the other five.
 *   2. `confirm_destructive` on a production org requires typing the org name (§8.2 rule 12). That is
 *      a gate, so it belongs somewhere a test can reach.
 *   3. Every card states WHAT HAPPENS NEXT before the user commits. That sentence is per-kind and
 *      per-blocking, which is exactly the combination a component tends to get subtly wrong.
 *
 * The suite runs in node with no DOM and no testing-library, so a rule that lives in a component is
 * a rule that ships untested. These are the parts worth testing; the card renders them.
 *
 * NOT COPY FOR SIX OF EIGHT KINDS' DATA. `clarify`, `approve_tool`, `confirm_destructive`,
 * `approve_budget`, `provide_input` and `auth_required` have never been observed on the wire (handoff
 * T2 — only `approve_plan` and `approve_changeset` appear in captures). The copy below is written to
 * the contract's documented shapes; nothing here asserts a field that has not been seen.
 */

import {
  asEvidenceNotReady,
  asInteractionClosed,
  asInteractionsUnavailable,
  asOverrideNotBound,
  asOverrideStale,
} from "./interactions";
import { describeLaneV2Error, isLaneV2Error } from "./laneV2/api";
import type { ClarifyAnswer, ClarifyCandidate, ClarifyQuestion, Interaction, IxKind } from "./protocol";

/** How the card should present itself. Semantic, not a colour — the component maps these to tokens. */
export type InteractionTone = "ask" | "approve" | "danger" | "budget" | "auth";

export interface InteractionCopy {
  /** Card heading. A question or an instruction, never a bare kind name. */
  title: string;
  /** One sentence: what answering this does. Rendered before the controls, not after. */
  consequence: string;
  tone: InteractionTone;
  /** Label for the affirmative control when the server offered no options at all. */
  fallbackAffirmative: string;
}

/** The kinds added in contracts 0.2.15 (the new ask lane). */
type NewIxKind =
  | "approve_plan_revision"
  | "approve_amendment"
  | "approve_overwrite"
  | "approve_delete"
  | "approve_apply"
  | "confirm_revert"
  | "choose_substitute"
  | "clarify_scope";

const LEGACY_COPY: Record<Exclude<IxKind, NewIxKind | ExtensionIxKind>, InteractionCopy> = {
  clarify: {
    title: "Which did you mean?",
    consequence: "Answering continues this run from where it paused — no new run is started.",
    tone: "ask",
    fallbackAffirmative: "Send",
  },
  approve_plan: {
    title: "Approve this plan?",
    consequence:
      "Approving starts building from this plan. Nothing changes in your org yet.",
    tone: "approve",
    fallbackAffirmative: "Approve plan",
  },
  approve_changeset: {
    title: "Approve these changes?",
    consequence:
      "Approving records your decision. Nothing reaches your org from here.",
    tone: "approve",
    fallbackAffirmative: "Approve",
  },
  approve_tool: {
    title: "Allow this tool to run?",
    consequence: "Allowing lets the agent run this tool with the arguments shown.",
    tone: "approve",
    fallbackAffirmative: "Allow once",
  },
  confirm_destructive: {
    title: "This will delete metadata",
    consequence:
      "Confirming deletes the components listed below. A checkpoint is taken first, so this can be reverted.",
    tone: "danger",
    fallbackAffirmative: "Confirm",
  },
  approve_budget: {
    title: "This run is near its spend limit",
    consequence: "Extending raises the ceiling for this run only. Stopping keeps what has run so far.",
    tone: "budget",
    fallbackAffirmative: "Extend",
  },
  provide_input: {
    title: "The agent needs a value",
    consequence: "Your answer is used by the step that asked for it, then the run continues.",
    tone: "ask",
    fallbackAffirmative: "Submit",
  },
  auth_required: {
    title: "Reconnect this org",
    consequence: "The run resumes once the connection is refreshed.",
    tone: "auth",
    fallbackAffirmative: "Reconnect",
  },
};

/**
 * The 0.2.15 kinds — the lane-v2 ASK kinds — get their OWN wording. They used to borrow the old
 * kind each replaces (MSA's `IX_KIND_OF` alias table), which put the wrong promise on screen:
 * `confirm_revert` read "This will delete metadata", `approve_plan_revision` "Approving starts a new
 * run". Lane-v2 asks belong to a TASK, not a run: they outlive the turn that raised them, expiry never
 * ends the work, and nothing here may promise a run starting or restarting. `approve_apply` must say
 * the org changes — it is the final human gate before a deploy.
 */
const NATIVE_COPY: Record<NewIxKind, InteractionCopy> = {
  approve_plan_revision: {
    title: "Approve this plan?",
    consequence:
      "Approving lets the task build exactly the items in this plan version. Nothing changes in your org yet.",
    tone: "approve",
    fallbackAffirmative: "Approve plan",
  },
  approve_amendment: {
    title: "Approve this change to the plan?",
    consequence:
      "Approving lets the task continue with the amended plan. Nothing changes in your org yet.",
    tone: "approve",
    fallbackAffirmative: "Approve change",
  },
  approve_overwrite: {
    title: "Overwrite what is in your org?",
    consequence:
      "Approving lets the task replace the org's current version in this change. Nothing is deployed from here.",
    tone: "danger",
    fallbackAffirmative: "Approve overwrite",
  },
  approve_delete: {
    title: "Delete these components from your org?",
    consequence:
      "Approving adds these deletions to the task's changeset. A deletion cannot always be undone — review the list before approving.",
    tone: "danger",
    fallbackAffirmative: "Approve deletion",
  },
  approve_apply: {
    title: "Apply these changes to your org?",
    consequence:
      "Approving deploys this validated changeset to your Salesforce org — this changes the org.",
    tone: "approve",
    fallbackAffirmative: "Apply to org",
  },
  confirm_revert: {
    title: "Revert the applied change?",
    consequence:
      "Confirming deploys the inverse of the applied change to your org, putting the listed components back the way they were before it was applied.",
    tone: "danger",
    fallbackAffirmative: "Confirm revert",
  },
  choose_substitute: {
    title: "Pick a substitute",
    consequence: "Your choice tells the task which component to use instead.",
    tone: "ask",
    fallbackAffirmative: "Send",
  },
  clarify_scope: {
    title: "Which did you mean?",
    consequence: "Your answer tells the task what is in scope.",
    tone: "ask",
    fallbackAffirmative: "Send",
  },
};

/**
 * Lane-v2 ask kinds the SERVER may raise AHEAD of the contracts tag this client's generated types are
 * pinned to (`scripts/codegen-weave-protocol.mjs` PINNED_TAG). `types.ts`'s rule — every reader
 * tolerates an enum value it has not seen — holds for the kind too, so a kind is named here the moment
 * the backend leg that raises it is designed, and moves into `NewIxKind` when the pin catches up
 * (the generated `IxKind` then carries it and this alias shrinks; nothing else changes).
 *
 * `approve_connector_write` (MSA leg `cv-connector-writes`): the user asked the chat to create, update or
 * comment on a page, issue or record in a CONNECTED external tool (Notion, Jira, …); the server asks
 * before writing. Its prompt's first line is the question ("Create the Notion page 'X'?"), the lines
 * after it the target and a preview — `promptLayout="full"` renders those verbatim, so no new card.
 */
export type ExtensionIxKind = "approve_connector_write";

const EXTENSION_COPY: Record<ExtensionIxKind, InteractionCopy> = {
  approve_connector_write: {
    title: "Write to a connected tool?",
    consequence:
      "Approving makes exactly this change in the tool named above. Nothing changes in your Salesforce org.",
    tone: "approve",
    fallbackAffirmative: "Approve write",
  },
};

const COPY: Record<IxKind | ExtensionIxKind, InteractionCopy> = { ...LEGACY_COPY, ...NATIVE_COPY, ...EXTENSION_COPY };

/**
 * The wording for a kind this client has NO entry for: a server newer than the client raised it. The
 * card still renders the server's own prompt, detail and options (those are the decision); only the
 * client's eyebrow and consequence are generic. Before this, `copyFor` returned `undefined` for an
 * unknown kind and the card's first `copy.title` read threw inside render — the route-level error
 * boundary then replaced the WHOLE Weave page with "Something went wrong" for one unfamiliar card.
 */
export const GENERIC_ASK_COPY: InteractionCopy = {
  title: "A decision is needed",
  consequence: "The question above says what happens. Choose one of the options below to continue.",
  tone: "ask",
  fallbackAffirmative: "Send",
};

/** The lane-v2 (contracts 0.2.15+) ask kinds. Their asks are owned by a TASK, never by a run. */
export const NATIVE_ASK_KINDS: ReadonlySet<string> = new Set<string>([
  ...(Object.keys(NATIVE_COPY) as NewIxKind[]),
  ...(Object.keys(EXTENSION_COPY) as ExtensionIxKind[]),
]);

export function isNativeAskKind(kind: string | null | undefined): boolean {
  return !!kind && NATIVE_ASK_KINDS.has(kind);
}

/**
 * Kinds that ASK the user something (rather than ask them to approve something). The card drops its
 * eyebrow and consequence line for these — the question is the heading and restating its form is
 * noise. One owner, so the old `clarify` and its lane-v2 successors cannot drift apart.
 */
export const CLARIFY_LIKE_KINDS: ReadonlySet<string> = new Set(["clarify", "clarify_scope", "choose_substitute"]);

export function isClarifyLike(kind: string | null | undefined): boolean {
  return !!kind && CLARIFY_LIKE_KINDS.has(kind);
}

/**
 * Who OWNS the question: a run (the old lane — the run is paused on it) or a task (lane v2 — the ask
 * outlives the turn that raised it). The eight native kinds are always task-owned; `provide_input` is
 * shared by both lanes, so for it the HOST says which (lane-v2 park asks are `provide_input` + `ack`).
 */
export type AskOwner = "run" | "task";

export function ownerOf(kind: string | null | undefined, host: AskOwner = "run"): AskOwner {
  return isNativeAskKind(kind) ? "task" : host;
}

/** `provide_input` raised by a lane-v2 task (a parked build): no run is involved. */
const TASK_PROVIDE_INPUT_COPY: InteractionCopy = {
  title: "The task needs your input",
  consequence: "Your answer goes to the task, which carries on from where it paused.",
  tone: "ask",
  fallbackAffirmative: "Continue",
};

/**
 * `kind` is `string`, not `IxKind`: the wire value is whatever the server sent, and a kind this
 * client predates must render (`GENERIC_ASK_COPY`), never throw. Every known kind keeps its own entry.
 */
export function copyFor(kind: string, owner: AskOwner = "run"): InteractionCopy {
  if (owner === "task" && kind === "provide_input") return TASK_PROVIDE_INPUT_COPY;
  return (COPY as Record<string, InteractionCopy | undefined>)[kind] ?? GENERIC_ASK_COPY;
}

/**
 * Does this question hold the run up?
 *
 * Reads `blocking` off the interaction. Deliberately a function taking the WHOLE interaction rather
 * than a `kind`, so there is no signature a caller could pass a bare kind to — the mistake this
 * module exists to prevent is exactly "assume from kind".
 */
export function holdsRunUp(interaction: Pick<Interaction, "blocking">): boolean {
  return interaction.blocking === true;
}

/**
 * Is this question bound to ONE plan step rather than to the run, plan or changeset as a whole?
 *
 * §5.4 D-P3 lets any kind carry `context_ref:{kind:"plan_step", plan_id, step_id}`, and §8.2 rule 20
 * says such a question renders AT THE STEP — never a modal, never a top-of-page banner. So there are
 * two possible mount points for one card, and exactly one of them must claim it: the transcript mount
 * skips a step-bound question, and the plan pane renders it.
 *
 * `PlanStep.interaction_id` is checked too, because §2.8 describes the binding from the step's side
 * as well. A question bound by one field but not the other would otherwise render nowhere.
 */
export function isStepBound(
  interaction: Pick<Interaction, "context_ref">,
  planSteps?: readonly { step_id: string; interaction_id?: string | null }[],
): boolean {
  const ref = interaction.context_ref as { kind?: string; id?: string } | null | undefined;
  if (ref?.kind === "plan_step") return true;
  if (!planSteps) return false;
  const id = (interaction as { interaction_id?: string }).interaction_id;
  return !!id && planSteps.some((step) => step.interaction_id === id);
}

/**
 * The status line under the title.
 *
 * Blocking and advisory are genuinely different situations and must not share a sentence: one means
 * the run is stopped until you answer, the other means the question outlives the run (§5.4 ²) — which
 * is what lets a user return the next day and approve a changeset with no live run.
 */
export function standingLine(
  interaction: Pick<Interaction, "blocking"> & { kind?: string | null },
  owner: AskOwner = "run",
): string {
  /*
   * A lane-v2 ask parks a TASK, not a run — and it outlives the turn that raised it. "This run is
   * paused" would be false twice over.
   */
  if (ownerOf(interaction.kind, owner) === "task") {
    return interaction.blocking === true
      ? "This task is waiting for your decision."
      : "The task continues — you can answer this later.";
  }
  return holdsRunUp(interaction)
    ? "This run is paused until you answer."
    : "The run continues — you can answer this later.";
}

/** The closed-card line for an expired question. A lane-v2 expiry never ends the work. */
export function expiredLine(kind: string | null | undefined, owner: AskOwner = "run"): string {
  return ownerOf(kind, owner) === "task"
    ? "This question expired. The task has not changed — ask again in the chat if it still matters."
    : "This question expired. Answering it now starts a new run.";
}

/**
 * Lane-v2 park-ask option ids (MSA `provide_input`). The server sends the LABELS and marks the recommended one; the ids only
 * pick behaviour here (text required, confirm step, submit verb). An id this client predates renders and submits as any option.
 */
export const CONTINUE_BUILT_OPTION_ID = "continue_built";
export const DESCRIBE_CHANGE_OPTION_ID = "describe_change";
export const CANCEL_TASK_OPTION_ID = "cancel_task";

/** Options that are destructive: selecting one asks for a second, explicit press before anything is sent. */
export const OPTIONS_REQUIRING_CONFIRM: ReadonlySet<string> = new Set([CANCEL_TASK_OPTION_ID]);

export function optionRequiresConfirm(optionId: string | null | undefined): boolean {
  return !!optionId && OPTIONS_REQUIRING_CONFIRM.has(optionId);
}

/** The consequence line under a park card's options when "Cancel the task" is the selection. */
export const CANCEL_TASK_CONSEQUENCE = "Cancelling ends this task. It will not carry on.";

/**
 * Options whose answer means nothing without an explanation. `changes_requested` (lane-v2 changeset
 * and apply asks) tells the task to rebuild — the text is WHAT to change, and MSA requires it.
 */
export const OPTIONS_REQUIRING_TEXT: ReadonlySet<string> = new Set(["changes_requested", DESCRIBE_CHANGE_OPTION_ID]);

export function optionRequiresText(optionId: string | null | undefined): boolean {
  return !!optionId && OPTIONS_REQUIRING_TEXT.has(optionId);
}

/** Kinds whose confirmation on a production org needs the org name typed (see below). */
const ORG_NAME_CONFIRM_KINDS: ReadonlySet<IxKind> = new Set<IxKind>([
  "confirm_destructive",
  "approve_overwrite",
  "approve_delete",
  "confirm_revert",
]);

/**
 * Must the user type the org name to confirm?
 *
 * §8.2 rule 12: destructive confirms on a production org require it. Both conditions, and only these
 * two — a destructive confirm on a sandbox does not, and a production org does not make every other
 * kind require typing. The destructive kinds are `confirm_destructive` and its 0.2.15 successors
 * `approve_overwrite`, `approve_delete` and `confirm_revert` (fail-safe: never less confirmation).
 */

export function requiresOrgNameConfirmation(
  kind: IxKind,
  org: { is_production?: boolean } | null | undefined,
): boolean {
  return ORG_NAME_CONFIRM_KINDS.has(kind) && org?.is_production === true;
}

/**
 * Is a submission answerable at all?
 *
 * Free text alone is valid whenever `allow_free_text` — the contract is explicit that a client
 * rendering nothing but a chat box still works (§5.4 universal rules), so an option must NOT be
 * required in that case. When free text is not allowed, an option is the only way to answer.
 */
export function canSubmit(
  interaction: Pick<Interaction, "allow_free_text"> & Partial<Pick<Interaction, "questions">>,
  draft: {
    optionId: string | null;
    freeText: string;
    /** A `candidates` pick (contracts 0.2.9) — answered with `ref_id`. */
    refId?: string | null;
    /** Batch answers keyed by `question_id` (contracts 0.2.9). */
    answers?: Record<string, ClarifyAnswer>;
  },
): boolean {
  /*
   * A BATCH is answered by its sub-questions, not by the card's own controls (0.2.9,
   * `validate_interaction_response`): every `required` question needs an answer, and an optional one
   * may be left out. The top-level free text is still a legal legacy answer to the whole batch when
   * the interaction allows it, so a reader who ignores the form and types is not blocked.
   */
  const questions = interaction.questions ?? null;
  if (questions && questions.length) {
    if (batchComplete(questions, draft.answers ?? {})) return true;
    return interaction.allow_free_text === true && draft.freeText.trim().length > 0;
  }
  if (draft.optionId) {
    // An option that needs an explanation is not an answer until it has one.
    return optionRequiresText(draft.optionId) ? draft.freeText.trim().length > 0 : true;
  }
  if (draft.refId) return true;
  return interaction.allow_free_text === true && draft.freeText.trim().length > 0;
}

/** Does this answer say anything? The contract's own rule: at least one of option_id, ref_id, free_text. */
export function answerGiven(answer: ClarifyAnswer | null | undefined): boolean {
  if (!answer) return false;
  if (answer.option_id) return true;
  if (answer.ref_id) return true;
  return typeof answer.free_text === "string" && answer.free_text.trim().length > 0;
}

/** Every `required` sub-question has an answer. Optional ones may be skipped (0.2.9). */
export function batchComplete(
  questions: readonly ClarifyQuestion[],
  answers: Record<string, ClarifyAnswer>,
): boolean {
  return questions.every((q) => q.required === false || answerGiven(answers[q.question_id]));
}

/**
 * The option a batch sub-question pre-selects: its `default_option_id` when it names one of its own
 * options, else the recommended one. Never auto-submitted — same rule as `preselectedOptionId`.
 */
export function preselectedQuestionOptionId(question: ClarifyQuestion): string | null {
  const options = question.options ?? [];
  if (question.default_option_id && options.some((o) => o.option_id === question.default_option_id)) {
    return question.default_option_id;
  }
  return options.find((o) => o.recommended === true)?.option_id ?? null;
}

// ── The 0.2.9 text fallback, ported so it can be recognised ─────────────────

function oneLine(text: string): string {
  return text.split(/\s+/).filter(Boolean).join(" ");
}

function candidateLine(candidates: readonly ClarifyCandidate[] | null | undefined): string | null {
  if (!candidates?.length) return null;
  return candidates.map((c) => oneLine(c.label)).join(", ");
}

function questionLines(number: number, question: ClarifyQuestion): string[] {
  let head = `${number}. ${oneLine(question.prompt)}`;
  if (question.required === false) head += " (optional)";
  const lines = [head];
  const indent = " ".repeat(`${number}. `.length);
  if (question.why) lines.push(`${indent}Why: ${oneLine(question.why)}`);
  if (question.options?.length) {
    const labels = question.options.map((option) => {
      let label = oneLine(option.label);
      if (option.option_id === question.default_option_id) label += " (default)";
      return label;
    });
    lines.push(`${indent}Options: ${labels.join(" / ")}`);
  }
  const candidates = candidateLine(question.candidates);
  if (candidates) lines.push(`${indent}Candidates: ${candidates}`);
  if (question.assumption_if_unanswered) {
    lines.push(`${indent}If unanswered: ${oneLine(question.assumption_if_unanswered)}`);
  }
  return lines;
}

/**
 * A byte-for-byte port of `backend_contracts.weave.authoring.clarify.compose_clarify_detail`
 * (contracts 0.2.9).
 *
 * WHY A CLIENT NEEDS THE PRODUCER'S FUNCTION. A 0.2.9 producer fills an EMPTY `detail` with this
 * text so a client built before 0.2.9 still sees the why / impact / candidates / sub-questions.
 * This client renders those fields structurally, so showing the composed `detail` as well would
 * print every fact twice. The contract's rule is exact: "a new client renders the structured fields
 * and does not also show a `detail` equal to `compose_clarify_detail(interaction)`". The function
 * is deterministic, so the comparison is a string equality — which is only possible if the port is
 * exact, and why nothing here is paraphrased.
 *
 * Returns `null` when the interaction carries none of the 0.2.9 fields.
 */
export function composeClarifyDetail(
  interaction: Pick<Interaction, "why" | "impact" | "candidates" | "questions" | "assumption_if_unanswered">,
): string | null {
  const blocks: string[] = [];
  if (interaction.why) blocks.push(`Why I'm asking: ${oneLine(interaction.why)}`);
  if (interaction.impact) blocks.push(`What it changes: ${oneLine(interaction.impact)}`);
  const candidates = candidateLine(interaction.candidates);
  if (candidates) blocks.push(`Candidates: ${candidates}`);
  if (interaction.questions?.length) {
    const lines = ["Questions:", ""];
    interaction.questions.forEach((question, index) => {
      lines.push(...questionLines(index + 1, question));
    });
    blocks.push(lines.join("\n"));
  }
  if (interaction.assumption_if_unanswered) {
    blocks.push(`If you don't answer, I will proceed with: ${oneLine(interaction.assumption_if_unanswered)}`);
  }
  return blocks.length ? blocks.join("\n\n") : null;
}

/**
 * The `detail` worth showing, or `null`.
 *
 * Hidden when it is exactly the producer's composed fallback (see `composeClarifyDetail`): the card
 * renders those facts as structured blocks, and a paragraph repeating them above the blocks is the
 * "form dropped into a chat" wall the card is trying not to be. An author-written `detail` — any
 * text that is NOT the composed fallback — is always shown.
 */
export function visibleDetail(interaction: Interaction): string | null {
  const detail = interaction.detail?.trim();
  if (!detail) return null;
  const composed = composeClarifyDetail(interaction);
  return composed !== null && detail === composed.trim() ? null : detail;
}

/** The option the card pre-selects, or `null`. NEVER auto-submitted — see `canSubmit`'s callers. */
export function preselectedOptionId(interaction: Interaction): string | null {
  const recommended = interaction.options.find((o) => o.recommended === true);
  if (recommended) return recommended.option_id;
  const fallback = interaction.default;
  if (typeof fallback === "string" && interaction.options.some((o) => o.option_id === fallback)) {
    return fallback;
  }
  return null;
}

/** Milliseconds until expiry; negative once past. `null` when the timestamp is unusable. */
export function msUntilExpiry(expiresAt: string, now: number): number | null {
  const at = Date.parse(expiresAt);
  return Number.isFinite(at) ? at - now : null;
}

/**
 * The deadline line, or `null` when there is nothing worth saying.
 *
 * Deliberately quiet until the last hour. A 24-hour clarify TTL counted down from the moment it opens
 * is noise that trains the reader to ignore the one case that matters; the handoff records that a
 * park with no visible deadline is what left 80+ runs stuck, and the fix is a deadline the user sees
 * WHEN IT IS CLOSE, not a permanent timer.
 */
export function expiryLine(expiresAt: string, now: number): string | null {
  const ms = msUntilExpiry(expiresAt, now);
  if (ms === null) return null;
  if (ms <= 0) return "This question has expired.";
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return "Expires in under a minute.";
  if (minutes < 60) return `Expires in ${minutes} minute${minutes === 1 ? "" : "s"}.`;
  return null;
}


// ── Failure copy ────────────────────────────────────────────────────────────

export interface RespondFailure {
  /** What to show the user. Never a bare status code. */
  message: string;
  /** The unmet evidence, when the failure was the gate — turns on the override affordance. */
  unmet: { evidence_id: string; kind: string }[] | null;
  /** True when the question is closed: the card should stop offering controls. */
  closed: boolean;
  /** True when the feature is switched off rather than broken — say nothing at all. */
  unavailable: boolean;
  /**
   * True on `409 OVERRIDE_STALE` (L-28): the evidence gate's obligations moved between when the
   * card rendered `unmetEvidence` and when the override was submitted. The caller (`PendingInteraction`)
   * re-fetches run state on this flag, exactly as it already does for a silently-finished run — see
   * that module's `onReconcile` call — so the retry is against the CURRENT set, not a replay of the
   * stale one.
   */
  staleOverride: boolean;
  /**
   * Lane v2: the answer was refused because what the card showed is no longer what the task is
   * waiting on (409 `STALE_SUBJECT`, `GRANT_STALE_HEAD`, `ANSWER_NOT_APPLICABLE`, an ask already
   * closed). The host re-reads the asks and the task snapshot; it NEVER retries on its own — the user
   * must look at the new proposal first.
   */
  staleSubject?: boolean;
}

/** Lane-v2 codes after which the card's view of the world is stale: refetch, never auto-retry. */
const LANE_V2_STALE_CODES: ReadonlySet<string> = new Set([
  "STALE_SUBJECT",
  "GRANT_STALE_HEAD",
  "STALE_TRANSITION",
  "ANSWER_NOT_APPLICABLE",
  "ASK_NOT_PENDING",
  "ASK_CONFLICT",
  "ASK_NOT_FOUND",
]);
/** Lane-v2 codes that CLOSE the question: nothing the user does on this card can answer it now. */
const LANE_V2_CLOSED_CODES: ReadonlySet<string> = new Set(["ASK_NOT_PENDING", "ASK_CONFLICT", "ASK_NOT_FOUND"]);

/**
 * `evd_<kind>_<hash16>` -> `<kind>`, mirroring the server's own `override_consent._kind_of`. A
 * machine-format token pulled from an id whose shape is fixed by spec, not a phrasing guess (§22
 * role iii) — used only to redraw the checklist `OVERRIDE_NOT_BOUND`/`OVERRIDE_STALE` hand back as
 * bare ids, with a label instead of a raw id.
 */
function kindOfEvidenceId(evidenceId: string): string {
  const body = evidenceId.startsWith("evd_") ? evidenceId.slice(4) : evidenceId;
  const cut = body.lastIndexOf("_");
  return (cut > 0 ? body.slice(0, cut) : body) || evidenceId;
}

/**
 * Turn a thrown respond error into something a person can act on.
 *
 * Pure, and separate from the hook that calls it, because THIS is the part worth testing: each of
 * the four branches leads the user somewhere different, and §10 of the frontend rules forbids copy
 * that claims what it cannot know.
 *
 * The two rules that are easy to get wrong:
 *
 *   - `EVIDENCE_NOT_READY` leaves the question PENDING (§5.4, checked before resolution). So the copy
 *     must not say the approval failed — it says the checks have not finished, and the card stays.
 *   - An unrecognised failure gets FIXED copy, not the server's body. An arbitrary 5xx body puts
 *     backend internals on screen (§10); the status is the only part safe to relay.
 */
export function describeRespondFailure(err: unknown): RespondFailure {
  /*
   * Lane v2 (`POST /api/sessions/{s}/asks/{a}/respond`). Checked first: its codes are its own and
   * none of the run-keyed narrowers below would recognise them. `DEPLOY_NOT_ENABLED` leaves the ask
   * OPEN (decline / request changes still work), so it is neither closed nor stale.
   */
  if (isLaneV2Error(err)) {
    const code = err.code ?? "";
    return {
      message: describeLaneV2Error(err),
      unmet: null,
      closed: LANE_V2_CLOSED_CODES.has(code),
      unavailable: false,
      staleOverride: false,
      staleSubject: LANE_V2_STALE_CODES.has(code),
    };
  }
  if (asInteractionsUnavailable(err)) {
    return {
      message: "",
      unmet: null,
      closed: false,
      // Nothing to say. A banner here would report a failure on a deployment that simply has the
      // feature switched off.
      unavailable: true,
      staleOverride: false,
    };
  }

  const gate = asEvidenceNotReady(err);
  if (gate) {
    return {
      message: gate.message,
      unmet: gate.unmet.map(({ evidence_id, kind }) => ({ evidence_id, kind })),
      // Deliberately NOT closed: the gate is checked before resolution, so the question survives and
      // the user can still answer it — with an override reason, or once the checks land.
      closed: false,
      unavailable: false,
      staleOverride: false,
    };
  }

  // L-28: the override named nothing it waives (no `override_unmet` sent, or the package could not
  // be identified). 422 — re-sending the SAME body will never work. The server still hands back the
  // live unmet set, so the checklist stays populated rather than going blank.
  const notBound = asOverrideNotBound(err);
  if (notBound) {
    return {
      message: notBound.message,
      unmet: notBound.unmet.map((evidence_id) => ({ evidence_id, kind: kindOfEvidenceId(evidence_id) })),
      closed: false,
      unavailable: false,
      staleOverride: false,
    };
  }

  // L-28: the override named a set the gate no longer recognises — its obligations moved between
  // render and click. Sibling of `EVIDENCE_NOT_READY`: the request was fine, the world moved, so the
  // caller re-fetches run state (`staleOverride`) and the user retries against the current set.
  const stale = asOverrideStale(err);
  if (stale) {
    return {
      message: stale.message,
      unmet: stale.unmet.map((evidence_id) => ({ evidence_id, kind: kindOfEvidenceId(evidence_id) })),
      closed: false,
      unavailable: false,
      staleOverride: true,
    };
  }

  const shut = asInteractionClosed(err);
  if (shut) {
    return {
      message:
        shut.code === "INTERACTION_EXPIRED"
          ? "This question expired. Sending your answer again will start a new run."
          : "Somebody already answered this question.",
      unmet: null,
      closed: true,
      unavailable: false,
      staleOverride: false,
    };
  }

  const status = err instanceof Error && "status" in err ? (err as { status?: unknown }).status : null;
  return {
    // Fixed copy plus the status. It says what is known ("not sent"), what is not ("why"), and that
    // retrying is safe — because a respond that never reached the server changed nothing.
    message:
      typeof status === "number"
        ? `Your answer was not sent (${status}). Nothing was changed — you can try again.`
        : "Your answer was not sent. Nothing was changed — you can try again.",
    unmet: null,
    closed: false,
    unavailable: false,
    staleOverride: false,
  };
}


/**
 * The verb on the submit button, for the option the reader has actually SELECTED.
 *
 * WHY NOT JUST THE PER-KIND AFFIRMATIVE. The card used `copy.fallbackAffirmative` unconditionally, so
 * on an `approve_changeset` with "Cancel" selected the button read **Approve** and submitted
 * `{option_id:"reject"}` — a control that says the opposite of what it does. Same for "Validate only
 * (checkOnly)", where "Approve" claims a deploy is being armed when a check-only run is.
 *
 * WHY NOT THE OPTION'S OWN LABEL EITHER. That was the previous behaviour and it was worse: the CTA
 * became a metadata name ("Opportunity_Line__c" as a button), which says what was picked rather than
 * what pressing it does — and it made the label unstable enough that a locator matching on it missed
 * the button mid-submit, which is how a double-submit test came to pass while its extra clicks were
 * hitting nothing.
 *
 * SO IT IS KEYED ON DISSENT, NOT ON APPROVAL. The first version of this function allowlisted the
 * affirmative ids and defaulted everything else to "Submit" — which broke `clarify`, whose
 * `opt_1`/`opt_2` ids are alternative ANSWERS rather than a refusal: "Send" is right for every one of
 * them, and eight harness checks that clicked "Send" went red. The set below is the contract's own
 * dissent-and-alternative vocabulary (§5.4's option tables), and every id outside it keeps the
 * kind's affirmative verb.
 *
 * Callers must locate the button by role + `data-interaction-submit`, never by this string.
 */
const DISSENTING_OPTIONS: ReadonlySet<string> = new Set([
  // A refusal, on every kind that offers one.
  "reject",
  "cancel",
  "deny",
  // `approve_budget`: both stop the work rather than extending it.
  "stop_here",
  "stop_and_answer",
  // `approve_changeset`: a checkOnly run is not the deploy the affirmative verb claims.
  "validate_only",
  // `approve_plan`: "Edit first" is a detour, not an approval.
  "edit",
  // Lane v2 (MSA `core/asks.py`): the one non-granting option on every authorize ask, and the
  // "rebuild with these changes" option on changeset / apply asks. Neither is the kind's approval.
  "decline",
  "changes_requested",
]);

/** Exported so a test can assert the lane-v2 dissent ids are in it. */
export function isDissentingOption(optionId: string | null | undefined): boolean {
  return !!optionId && DISSENTING_OPTIONS.has(optionId);
}

/**
 * Lane-v2 park asks (`provide_input`): `continue_built`, `describe_change`, `cancel_task`, or the older single `ack`
 * (carry on, with optional guidance). An old server sends only `ack`; this client renders whatever options arrive.
 */
export const ACK_OPTION_ID = "ack";

/** The consequence line for the current selection: a cancel on a park card must not say the task "carries on". */
export function consequenceForOption(base: string | null, kind: string, optionId: string | null | undefined): string | null {
  return kind === "provide_input" && optionId === CANCEL_TASK_OPTION_ID ? CANCEL_TASK_CONSEQUENCE : base;
}

/**
 * Does pressing this option's action button SEND at once? Only when the option is a complete answer on its own, nothing
 * gates the card, and it is not destructive (`cancel_task` selects first and sends on the second, explicit press).
 */
export function sendsOnPress(
  interaction: Parameters<typeof canSubmit>[0],
  optionId: string,
  gated: boolean,
): boolean {
  return canSubmit(interaction, { optionId, freeText: "", refId: null, answers: {} }) && !gated && !optionRequiresConfirm(optionId);
}

export function submitVerb(kind: string, optionId: string | null | undefined, owner: AskOwner = "run"): string {
  const copy = copyFor(kind, owner);
  if (!optionId) return copy.fallbackAffirmative;
  if (optionId === ACK_OPTION_ID || optionId === CONTINUE_BUILT_OPTION_ID) return "Continue";
  if (optionId === DESCRIBE_CHANGE_OPTION_ID) return "Submit";
  if (optionId === CANCEL_TASK_OPTION_ID) return "Cancel task";
  return DISSENTING_OPTIONS.has(optionId) ? "Submit" : copy.fallbackAffirmative;
}
